import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { generatePath } from 'react-router';

import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import {
  MY_SORARE_OFFERS_RECEIVED_ID,
  MY_SORARE_OFFERS_SENT_ID,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { isType } from '@sorare/core/src/lib/gql';

import { DumbNotification } from 'components/activity/DumbNotification';

import { commonNotificationInterfaceFragment } from '../fragments';
import type { CommonNotificationProps } from '../types';
import type { OfferNotification_offerNotification } from './__generated__/index.graphql';

type Props = CommonNotificationProps & {
  notification: OfferNotification_offerNotification;
};

type OfferNotification_tokenOffer_User = (
  | OfferNotification_offerNotification['tokenOffer']['receiver']
  | OfferNotification_offerNotification['tokenOffer']['sender']
) & {
  __typename: 'User';
};

export const OfferNotification = ({ notification, ...rest }: Props) => {
  const { isMe } = useCurrentUserContext();

  const { name, createdAt, sport, tokenOffer, read } = notification;

  const link = useMemo(() => {
    if (isType(tokenOffer.sender, 'User') && isMe(tokenOffer.sender)) {
      return generatePath(MY_SORARE_OFFERS_SENT_ID, {
        id: idFromObject(tokenOffer.id),
      });
    }
    return generatePath(MY_SORARE_OFFERS_RECEIVED_ID, {
      id: idFromObject(tokenOffer.id),
    });
  }, [isMe, tokenOffer]);

  let user;
  switch (name) {
    case 'offer_accepted':
    case 'offer_rejected':
    case 'offer_cancelled':
      user = tokenOffer.receiver as OfferNotification_tokenOffer_User;
      break;
    case 'offer_received':
    case 'offer_countered':
    case 'offer_withdrawn':
      user = tokenOffer.sender as OfferNotification_tokenOffer_User;
      break;
    default:
  }

  return (
    <DumbNotification
      name={name}
      title={<LazyMarkdown data={notification.markdownBody} />}
      userAvatar={user}
      link={link}
      createdAt={createdAt}
      sport={sport}
      read={read}
      {...rest}
    />
  );
};

OfferNotification.fragments = {
  offerNotification: gql`
    fragment OfferNotification_offerNotification on OfferNotification {
      id
      ...Notification_notificationInterface
      sport
      tokenOffer {
        id
        status
        sender {
          ... on User {
            slug
            nickname
            ...DumbNotification_userAvatar
          }
          ... on AnonymousUser {
            id
          }
        }
        rejectionReason
        receiver {
          ... on User {
            slug
            nickname
            ...DumbNotification_userAvatar
          }
          ... on AnonymousUser {
            id
          }
        }
      }
    }
    ${commonNotificationInterfaceFragment}
    ${DumbNotification.fragments.avatarUser}
  ` as TypedDocumentNode<OfferNotification_offerNotification>,
};
